import { useState, useEffect, useCallback, useRef } from "react";
import { connectSocket, getSocket, disconnectSocket } from "../utils/socket";

/**
 * Custom hook for chat functionality
 * Manages socket connection, room joining, messaging, and typing indicators
 */
export function useChat({ token, eventId, webinarId, userName, email, role }) {
  const [connected, setConnected] = useState(false);
  const [roomInfo, setRoomInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [error, setError] = useState(null);
  const typingTimeoutRef = useRef(null);

  // Connect and join room
  useEffect(() => {
    if (!token || !userName) return;

    const socket = connectSocket(token);

    const onConnect = () => {
      setConnected(true);
      setError(null);

      // Join room
      socket.emit("joinRoom", {
        eventId: eventId || undefined,
        webinarId: webinarId || undefined,
        userName,
        email: email || undefined,
        role: role || "ATTENDEE",
      });
    };

    const onDisconnect = () => {
      setConnected(false);
    };

    const onRoomJoined = (data) => {
      setRoomInfo(data);
      setParticipantCount(data.onlineCount);
      setParticipants(data.participants || []);
    };

    const onMessageHistory = (history) => {
      setMessages(history);
    };

    const onNewMessage = (msg) => {
      setMessages((prev) => [...prev, msg]);
    };

    const onMessageDeleted = ({ messageId }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, isDeleted: true } : m
        )
      );
    };

    const onUserJoined = ({ userId, userName: name, role: userRole }) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `system-${Date.now()}`,
          messageType: "SYSTEM",
          message: `${name} joined the chat 🎉`,
          createdAt: new Date().toISOString(),
          isSystem: true,
        },
      ]);
    };

    const onUserLeft = ({ userId, userName: name }) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `system-${Date.now()}`,
          messageType: "SYSTEM",
          message: `${name} left the chat`,
          createdAt: new Date().toISOString(),
          isSystem: true,
        },
      ]);
    };

    const onParticipantCount = (count) => {
      setParticipantCount(count);
    };

    const onUserTyping = ({ userName: name }) => {
      setTypingUsers((prev) => {
        if (!prev.includes(name)) return [...prev, name];
        return prev;
      });
      // Auto-remove after 3 seconds
      setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => u !== name));
      }, 3000);
    };

    const onUserStopTyping = ({ userName: name }) => {
      setTypingUsers((prev) => prev.filter((u) => u !== name));
    };

    const onError = (err) => {
      setError(err.message);
      console.error("Chat error:", err.message);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("roomJoined", onRoomJoined);
    socket.on("messageHistory", onMessageHistory);
    socket.on("newMessage", onNewMessage);
    socket.on("messageDeleted", onMessageDeleted);
    socket.on("userJoined", onUserJoined);
    socket.on("userLeft", onUserLeft);
    socket.on("participantCount", onParticipantCount);
    socket.on("userTyping", onUserTyping);
    socket.on("userStopTyping", onUserStopTyping);
    socket.on("error", onError);

    // If already connected, trigger join manually
    if (socket.connected) {
      onConnect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("roomJoined", onRoomJoined);
      socket.off("messageHistory", onMessageHistory);
      socket.off("newMessage", onNewMessage);
      socket.off("messageDeleted", onMessageDeleted);
      socket.off("userJoined", onUserJoined);
      socket.off("userLeft", onUserLeft);
      socket.off("participantCount", onParticipantCount);
      socket.off("userTyping", onUserTyping);
      socket.off("userStopTyping", onUserStopTyping);
      socket.off("error", onError);

      const s = getSocket();
      if (s) {
        s.emit("leaveRoom");
      }
      disconnectSocket();
    };
  }, [token, eventId, webinarId, userName, email, role]);

  // Send a message
  const sendMessage = useCallback(
    (text) => {
      const socket = getSocket();
      if (!socket || !roomInfo) return;

      socket.emit("sendMessage", {
        roomId: roomInfo.roomId,
        message: text,
        messageType: "TEXT",
      });

      // Stop typing when sending
      socket.emit("stopTyping", { roomId: roomInfo.roomId });
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    },
    [roomInfo]
  );

  // Delete a message
  const deleteMessage = useCallback((messageId) => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit("deleteMessage", { messageId });
  }, []);

  // Emit typing indicator
  const emitTyping = useCallback(() => {
    const socket = getSocket();
    if (!socket || !roomInfo) return;

    socket.emit("typing", { roomId: roomInfo.roomId });

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("stopTyping", { roomId: roomInfo.roomId });
    }, 1500);
  }, [roomInfo]);

  return {
    connected,
    roomInfo,
    messages,
    typingUsers,
    participants,
    participantCount,
    error,
    sendMessage,
    deleteMessage,
    emitTyping,
  };
}
